import {
  UpdateType,
  type AbstractPowerSyncDatabase,
  type PowerSyncBackendConnector,
} from '@powersync/common';
import { type SupabaseClient } from '@supabase/supabase-js';

import { redactError } from '@rbf/shared';

import { resolveDevHost } from '../dev-host';
import { Sentry } from '../sentry';
import { supabase } from '../supabase';

// The CRUD replay below targets tables by dynamic name, so use an untyped view
// of the client (the generated Database types require literal table names).
const untyped = supabase as unknown as SupabaseClient;

const powersyncUrl = process.env.EXPO_PUBLIC_POWERSYNC_URL;

if (!powersyncUrl) {
  throw new Error('Missing EXPO_PUBLIC_POWERSYNC_URL (see apps/mobile/.env.example).');
}

const POWERSYNC_URL = resolveDevHost(powersyncUrl);

/**
 * Postgres SQLSTATEs that no amount of retrying will fix — the write is
 * permanently rejected (RLS denial, constraint/trigger violation, bad input).
 * On these we DISCARD the transaction so a single poisoned op can't wedge the
 * upload queue in an infinite retry loop. Transient errors (network, conflict)
 * fall through to a re-throw, which makes PowerSync retry.
 */
const PERMANENT_PG_CODES = new Set([
  '42501', // insufficient_privilege — RLS rejection
  '23502', // not_null_violation
  '23503', // foreign_key_violation
  '23505', // unique_violation
  '23514', // check_violation — e.g. the freeze trigger on a completed instance
  '22P02', // invalid_text_representation
  'P0001', // raise_exception — our freeze/ownership triggers
]);

export function isPermanentError(err: unknown): boolean {
  const code = (err as { code?: string } | null)?.code;
  return typeof code === 'string' && PERMANENT_PG_CODES.has(code);
}

/**
 * A failed `fetch` while offline: no server reached, so there's no PG `.code`.
 * We still re-throw (PowerSync retries on reconnect), but log it quietly — these
 * fire on every write while offline and aren't real errors. The failure reaches
 * us as a `TypeError` whose `message` carries `"Failed to fetch"` (web) /
 * `"Network request failed"` (RN native), or as the Supabase client's wrapped
 * error carrying the same string, so match the message. (Matching on the
 * `TypeError` name alone would also swallow genuine programming bugs.)
 */
export function isNetworkError(err: unknown): boolean {
  const message = (err as { message?: string } | null)?.message ?? '';
  return /failed to fetch|network request failed/i.test(message);
}

/** SQLite has no boolean; coerce 0/1 back to a real boolean before upload. */
export function coerceForPostgres(
  table: string,
  data: Record<string, unknown>,
): Record<string, unknown> {
  if (table === 'instance_items' && 'checked' in data && data.checked != null) {
    return { ...data, checked: Boolean(data.checked) };
  }
  return data;
}

/**
 * Connects the local PowerSync DB to Supabase:
 *  - fetchCredentials: hands PowerSync the service endpoint + the user's
 *    Supabase access token (PowerSync validates it with the shared JWT secret).
 *  - uploadData: replays the local write queue against Supabase through the
 *    authenticated client, so RLS applies exactly as it would online.
 */
export class SupabaseConnector implements PowerSyncBackendConnector {
  async fetchCredentials() {
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    const session = data.session;
    if (!session) return null;
    return {
      endpoint: POWERSYNC_URL as string,
      token: session.access_token,
    };
  }

  async uploadData(database: AbstractPowerSyncDatabase): Promise<void> {
    const transaction = await database.getNextCrudTransaction();
    if (!transaction) return;

    // Apply ops one by one. A PERMANENT rejection discards only that op and lets
    // the rest of the transaction proceed — a whole-transaction abort here would
    // ACK sibling ops that were never sent (e.g. startInstance writes the run
    // header + every item in one transaction), truncating the run server-side and
    // then locally on the next reconcile. A TRANSIENT/offline error aborts the
    // loop WITHOUT completing, so PowerSync retries the whole transaction; the
    // already-applied ops replay idempotently (PUT is ON CONFLICT DO NOTHING;
    // PATCH/DELETE are keyed by id).
    for (const op of transaction.crud) {
      // Identifiers only — never log opData, which carries user content (note
      // bodies, step labels).
      const opInfo = { table: op.table, op: op.op, id: op.id };
      try {
        const table = untyped.from(op.table);
        const data = coerceForPostgres(op.table, op.opData ?? {});

        let result;
        switch (op.op) {
          case UpdateType.PUT:
            // A PUT is a locally-created row (edits arrive as PATCH), so it only
            // ever needs to INSERT. Merging on conflict (the supabase-js default)
            // would additionally demand UPDATE, which append-only tables like
            // instance_item_notes withhold by grant — so a create there 403s with
            // "permission denied for table" (42501). ON CONFLICT DO NOTHING keeps
            // retries idempotent while needing only INSERT.
            result = await table.upsert(
              { ...data, id: op.id },
              { onConflict: 'id', ignoreDuplicates: true },
            );
            break;
          case UpdateType.PATCH:
            // .select('id') so a zero-row match is visible (see below); id only,
            // never the row content.
            result = await table.update(data).eq('id', op.id).select('id');
            break;
          case UpdateType.DELETE:
            result = await table.delete().eq('id', op.id).select('id');
            break;
        }
        if (result?.error) throw result.error;
        if (op.op !== UpdateType.PUT && result?.data?.length === 0) {
          // A PATCH/DELETE that RLS USING filters to zero rows returns the SAME
          // shape as success (204, error:null) — e.g. a queued check uploading
          // after the member's template access was revoked. The write is gone
          // and the local row will be overwritten from the server on the next
          // reconcile, so report it. Do NOT throw: PowerSync legitimately
          // replays a PATCH whose row was since deleted server-side and a
          // DELETE that already succeeded, and a throw would wedge the upload
          // queue retrying an op that can never match.
          console.warn('PowerSync upload matched no rows; write did not apply', opInfo);
          Sentry.captureMessage('PowerSync upload matched no rows', {
            level: 'warning',
            tags: { area: 'powersync-upload', outcome: 'no-rows' },
            extra: opInfo,
          });
        }
      } catch (err) {
        if (isPermanentError(err)) {
          // Permanent rejection (RLS, constraint, freeze trigger): drop this one
          // op so it can't wedge the queue in a retry loop, and keep applying the
          // rest. The local row stays as the user left it; the next reconnect
          // re-reconciles it from the server.
          //
          // The drop is silent to the user, so report it to telemetry.
          console.warn('PowerSync upload permanently rejected; discarding op', opInfo);
          Sentry.captureException(err, {
            tags: { area: 'powersync-upload', outcome: 'discarded' },
            extra: opInfo,
          });
          continue;
        }
        if (isNetworkError(err)) {
          // Offline — expected while disconnected; the write stays queued locally.
          console.debug('PowerSync upload deferred (offline); will retry', op.table);
        } else {
          // redactError: a Postgres constraint error can echo the uploaded row's
          // content (note bodies, step labels) in its details — log a redacted
          // summary, not the raw error.
          console.error(
            'PowerSync upload failed for op (will retry)',
            opInfo,
            redactError(err),
          );
        }
        // Transient error — re-throw WITHOUT completing so PowerSync retries the
        // whole transaction on reconnect.
        throw err;
      }
    }

    await transaction.complete();
  }
}
